/**
 * JournalDraftPanel.tsx
 * Extracted from JournalPanel.tsx — DraftsView + DraftEditor components.
 * Zero logic changes. Props identical to original private component signatures.
 */
import React from 'react';
import { JournalDraft, JournalDecision, ExecutionQuality, EmotionState, JournalDraftOutcome } from '../../../types';

// ─── Quick-pick decision reasons ─────────────────────────────────────────────
const REASON_CHIPS = [
  'Confirmation missing', 'Countertrend risk', 'Low volume / RVOL',
  'Too extended', 'Already moved', 'Trap risk', 'Personal risk limit',
  'Higher TF conflict', 'Waiting for BOS/MSS', 'Waiting for RVOL',
];

const lbl = (txt: string) => (
  <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)',
    textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 3 }}>
    {txt}
  </span>
);

const ro = (label: string, value: string | null | undefined | boolean, color?: string) => {
  if (value === null || value === undefined || value === '') return null;
  const display = typeof value === 'boolean' ? (value ? 'YES' : 'NO') : String(value);
  return (
    <div style={{ marginBottom: 5 }}>
      {lbl(label)}
      <span style={{ fontSize: 11, color: color ?? 'var(--text)', fontFamily: 'var(--mono)' }}>{display}</span>
    </div>
  );
};

function DraftEditor({ draft, onUpdate, onDelete }: {
  draft: JournalDraft;
  onUpdate: (id: string, patch: Partial<JournalDraft>) => void;
  onDelete: (id: string) => void;
}) {
  const upd = (patch: Partial<JournalDraft>) => onUpdate(draft.id, patch);

  const sectionHead = (icon: string, title: string) => (
    <div style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--gold)',
      textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8, marginTop: 14,
      borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
      {icon} {title}
    </div>
  );

  const chip = (label: string, active: boolean, onClick: () => void, activeColor = 'var(--cyan)') => (
    <button key={label} onClick={onClick} style={{
      fontSize: 9, fontFamily: 'var(--mono)', padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
      border: `1px solid ${active ? activeColor : 'var(--border)'}`,
      background: active ? `${activeColor}22` : 'var(--surface2)',
      color: active ? activeColor : 'var(--blue-dim)', marginRight: 4, marginBottom: 4,
    }}>{label}</button>
  );

  const selectStyle: React.CSSProperties = {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 11,
    padding: '4px 8px', borderRadius: 3, width: '100%',
  };
  const textStyle: React.CSSProperties = { ...selectStyle, resize: 'vertical' as const, minHeight: 54 };

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 13, color: 'var(--gold)', marginBottom: 4 }}>{draft.title}</div>
      <div style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', marginBottom: 10 }}>
        {draft.symbol} · {new Date(draft.createdAt).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET
      </div>

      {sectionHead('📡', 'Market Context')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 14px', marginBottom: 6 }}>
        {ro('Session', draft.sessionPhase)}
        {ro('Price', draft.price != null ? `$${draft.price.toFixed(2)}` : null)}
        {ro('VWAP', draft.vwap != null ? `$${draft.vwap.toFixed(2)}` : null)}
        {ro('RVOL', draft.rvol != null ? `${draft.rvol.toFixed(2)}×` : null, draft.rvol != null && draft.rvol >= 1.2 ? 'var(--green)' : 'var(--amber)')}
        {ro('Regime', draft.regime)}
        {ro('SMC Bias', draft.smcBias, draft.smcBias === 'bullish' ? 'var(--green)' : draft.smcBias === 'bearish' ? 'var(--red)' : undefined)}
      </div>

      {sectionHead('🎯', 'Setup Truth')}
      <div style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 700,
          color: draft.setupActionable ? 'var(--green)' : 'var(--amber)',
          border: `1px solid ${draft.setupActionable ? 'var(--green-bd)' : 'var(--amber)44'}`,
          background: draft.setupActionable ? 'var(--green-bg)' : 'var(--surface2)',
          padding: '2px 8px', borderRadius: 3 }}>
          {draft.setupActionable ? '● ACTIONABLE' : '◐ NOT ACTIONABLE'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 14px', marginBottom: 4 }}>
        {ro('Display Setup', draft.displaySetupType)}
        {ro('Actionable Setup', draft.actionableSetupType, 'var(--cyan)')}
        {ro('Direction', draft.actionableSetupDir?.toUpperCase())}
        {ro('Maturity', draft.setupMaturity, draft.setupMaturity === 'actionable' ? 'var(--green)' : draft.setupMaturity === 'confirmed' ? 'var(--cyan)' : 'var(--amber)')}
        {ro('Alignment', draft.setupAlignment)}
        {ro('Countertrend', draft.countertrend, draft.countertrend ? 'var(--red)' : 'var(--green)')}
        {ro('Transition Confirmed', draft.stateTransitionConfirmed, draft.stateTransitionConfirmed ? 'var(--green)' : 'var(--amber)')}
      </div>
      {draft.confirmationMissing.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {lbl('Confirmation Missing')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {draft.confirmationMissing.map(m => (
              <span key={m} style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--amber)',
                border: '1px solid var(--amber)44', padding: '1px 6px', borderRadius: 2 }}>{m}</span>
            ))}
          </div>
        </div>
      )}

      {sectionHead('📋', 'Trade Plan')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 14px', marginBottom: 4 }}>
        {ro('Confidence', draft.setupConfidence != null ? `${draft.setupConfidence}%` : null)}
        {ro('T1', draft.t1)}
        {ro('T2', draft.t2)}
      </div>
      {draft.entry && <div style={{ marginBottom: 4 }}>{lbl('Entry')}<span style={{ fontSize: 10, color: 'var(--text)', fontFamily: 'var(--mono)' }}>{draft.entry}</span></div>}
      {draft.confirmation && <div style={{ marginBottom: 4 }}>{lbl('Confirmation')}<span style={{ fontSize: 10, color: 'var(--green)', fontFamily: 'var(--mono)' }}>{draft.confirmation}</span></div>}
      {draft.invalidation && <div style={{ marginBottom: 4 }}>{lbl('Invalidation')}<span style={{ fontSize: 10, color: 'var(--red)', fontFamily: 'var(--mono)' }}>{draft.invalidation}</span></div>}

      {draft.primaryThesis && (<>
        {sectionHead('🧠', 'AI Thesis')}
        <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--blue-muted)',
          background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 3,
          padding: '6px 8px', marginBottom: 6, lineHeight: 1.5 }}>{draft.primaryThesis}</div>
        {draft.keyDrivers.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            {lbl('Key Drivers')}
            {draft.keyDrivers.map((d, i) => (
              <div key={i} style={{ fontSize: 10, color: 'var(--text)', fontFamily: 'var(--mono)',
                paddingLeft: 8, borderLeft: '2px solid var(--border)', marginBottom: 2 }}>· {d}</div>
            ))}
          </div>
        )}
        {draft.uncertainty && <div style={{ fontSize: 10, color: 'var(--amber)', fontFamily: 'var(--mono)',
          marginBottom: 6 }}>{lbl('Uncertainty')}{draft.uncertainty}</div>}
      </>)}

      {sectionHead('💡', 'System Reasoning')}
      {draft.whyItMattered && (
        <div style={{ marginBottom: 6 }}>
          {lbl('Why this mattered')}
          <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--blue-muted)',
            background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 3,
            padding: '6px 8px', lineHeight: 1.5 }}>{draft.whyItMattered}</div>
        </div>
      )}
      <div style={{ marginBottom: 6 }}>
        {lbl('Why actionable or not')}
        <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--blue-muted)',
          background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 3,
          padding: '6px 8px', lineHeight: 1.5 }}>{draft.whyActionable}</div>
      </div>

      {sectionHead('✍️', 'My Decision')}
      <div style={{ marginBottom: 8 }}>
        {lbl('Decision *')}
        <div style={{ display: 'flex', gap: 6 }}>
          {(['took', 'passed', 'watching'] as JournalDecision[]).map(d => chip(
            d === 'took' ? '✓ TOOK' : d === 'passed' ? '✗ PASSED' : '👁 WATCHING',
            draft.decision === d,
            () => upd({ decision: d }),
            d === 'took' ? 'var(--green)' : d === 'passed' ? 'var(--red)' : 'var(--cyan)',
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        {lbl('Reason (pick + add notes)')}
        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 4 }}>
          {REASON_CHIPS.map(r => chip(r, draft.decisionReason.includes(r),
            () => {
              const cur = draft.decisionReason;
              const next = cur.includes(r)
                ? cur.replace(r, '').replace(/^,\s*|,\s*$|,\s*,/g, '').trim()
                : cur ? `${cur}, ${r}` : r;
              upd({ decisionReason: next });
            }
          ))}
        </div>
        <textarea style={textStyle} value={draft.decisionReason} rows={2}
          placeholder="Additional notes on decision reason…"
          onChange={e => upd({ decisionReason: e.target.value })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          {lbl('Execution Quality')}
          <select style={selectStyle} value={draft.executionQuality ?? ''}
            onChange={e => upd({ executionQuality: (e.target.value || null) as ExecutionQuality | null })}>
            <option value="">— optional —</option>
            <option value="good">Good</option><option value="ok">OK</option><option value="poor">Poor</option>
          </select>
        </div>
        <div>
          {lbl('Emotion State')}
          <select style={selectStyle} value={draft.emotionState ?? ''}
            onChange={e => upd({ emotionState: (e.target.value || null) as EmotionState | null })}>
            <option value="">— optional —</option>
            <option value="calm">Calm</option><option value="hesitant">Hesitant</option>
            <option value="fomo">FOMO</option><option value="frustrated">Frustrated</option>
            <option value="unclear">Unclear</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        {lbl('Trader Notes')}
        <textarea style={textStyle} value={draft.traderNotes} rows={3}
          placeholder="What did you observe? What would you do differently?"
          onChange={e => upd({ traderNotes: e.target.value })} />
      </div>

      {sectionHead('📊', 'Review Later')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          {lbl('Outcome')}
          <select style={selectStyle} value={draft.outcome}
            onChange={e => upd({ outcome: e.target.value as JournalDraftOutcome })}>
            <option value="pending">Pending</option>
            <option value="worked">Worked ✓</option><option value="failed">Failed ✗</option>
            <option value="invalidated">Invalidated</option><option value="no_entry">No Entry</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        {lbl('Outcome Notes')}
        <textarea style={textStyle} value={draft.outcomeNotes} rows={2}
          placeholder="What happened? What did the setup do?"
          onChange={e => upd({ outcomeNotes: e.target.value })} />
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <button className="btn btn-sm" style={{ fontSize: 10, color: 'var(--red)', borderColor: 'var(--red)44' }}
          onClick={() => { if (window.confirm('Delete this draft?')) onDelete(draft.id); }}>
          Delete
        </button>
        <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', alignSelf: 'center',
          flex: 1, paddingLeft: 4 }}>
          {draft.decision ? `Decision: ${draft.decision.toUpperCase()}` : 'Pick a decision to complete'}
        </span>
      </div>
    </div>
  );
}

export function JournalDraftPanel({ drafts, activeId, onSelect, onUpdate, onDelete }: {
  drafts: JournalDraft[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: Partial<JournalDraft>) => void;
  onDelete: (id: string) => void;
}) {
  const activeDraft = drafts.find(d => d.id === activeId) ?? drafts[0] ?? null;

  if (!drafts.length) {
    return (
      <div className="empty" style={{ paddingTop: 40 }}>
        <div style={{ fontSize: 28, opacity: .3 }}>📓</div>
        <h3 style={{ fontSize: 14 }}>No Journal Drafts</h3>
        <p style={{ fontSize: 12, color: 'var(--blue-dim)' }}>
          Click <strong style={{ color: 'var(--cyan)' }}>📓 Journal Draft</strong> in the AI Thesis panel
          to create a prefilled draft from the current setup + thesis.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, minHeight: 400 }}>
      <div style={{ borderRight: '1px solid var(--border)', paddingRight: 10 }}>
        <div style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)',
          textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
          {drafts.length} draft{drafts.length !== 1 ? 's' : ''}
        </div>
        {drafts.map(d => (
          <div key={d.id} onClick={() => onSelect(d.id)}
            style={{ cursor: 'pointer', padding: '6px 8px', borderRadius: 4, marginBottom: 4,
              background: (activeDraft?.id === d.id) ? 'var(--surface2)' : 'transparent',
              border: `1px solid ${activeDraft?.id === d.id ? 'var(--gold)' : 'transparent'}` }}>
            <div style={{ fontSize: 9, fontFamily: 'var(--mono)', fontWeight: 700,
              color: activeDraft?.id === d.id ? 'var(--gold)' : 'var(--text)', marginBottom: 2 }}>
              {d.symbol}
            </div>
            <div style={{ fontSize: 8, fontFamily: 'var(--mono)', color: 'var(--blue-dim)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>
              {d.title.replace(d.symbol + ' — ', '')}
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {d.decision && (
                <span style={{ fontSize: 7, fontFamily: 'var(--mono)',
                  color: d.decision === 'took' ? 'var(--green)' : d.decision === 'passed' ? 'var(--red)' : 'var(--cyan)',
                  border: `1px solid ${d.decision === 'took' ? 'var(--green-bd)' : d.decision === 'passed' ? 'var(--red)44' : 'var(--cyan)44'}`,
                  padding: '0 3px', borderRadius: 2 }}>
                  {d.decision.toUpperCase()}
                </span>
              )}
              {!d.setupActionable && (
                <span style={{ fontSize: 7, color: 'var(--amber)', fontFamily: 'var(--mono)',
                  border: '1px solid var(--amber)44', padding: '0 3px', borderRadius: 2 }}>WATCH</span>
              )}
              <span style={{ fontSize: 7, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', marginLeft: 'auto' }}>
                {new Date(d.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })}
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* maxHeight uses a fixed value — the Journal workspace container handles outer scroll */}
      <div style={{ overflowY: 'auto', maxHeight: '60vh', paddingRight: 4 }}>
        {activeDraft
          ? <DraftEditor draft={activeDraft} onUpdate={onUpdate} onDelete={onDelete} />
          : <div style={{ color: 'var(--blue-dim)', fontSize: 12, padding: 20 }}>Select a draft to review</div>
        }
      </div>
    </div>
  );
}
